import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import catalog from './product-catalog';

// Shopper region decides currency + payment gateway for the whole shop:
//   'IN'   → prices in ₹, pay with Cashfree
//   'INTL' → prices in US$, pay with PayPal
export const REGIONS = {
  IN: { id: 'IN', label: 'India', short: 'IN', currency: 'INR', symbol: '₹', gateway: 'Cashfree' },
  INTL: { id: 'INTL', label: 'International', short: 'US', currency: 'USD', symbol: '$', gateway: 'PayPal' },
};

const REGION_STORAGE_KEY = 'grafito_shop_region';

function detectRegion() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (/^Asia\/(Kolkata|Calcutta)$/.test(tz)) return 'IN';
    if (tz) return 'INTL';
  } catch {
    // fall through
  }
  return 'IN';
}

const RegionContext = createContext(null);

export function RegionProvider({ children }) {
  // Always render 'IN' on the server / first paint, then hydrate from storage.
  const [region, setRegionState] = useState('IN');
  const [chosen, setChosen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let stored = null;
    try {
      stored = window.localStorage.getItem(REGION_STORAGE_KEY);
    } catch {
      // storage blocked
    }
    if (stored === 'IN' || stored === 'INTL') {
      setRegionState(stored);
      setChosen(true);
    } else {
      setRegionState(detectRegion());
    }
  }, []);

  const setRegion = useCallback((next) => {
    if (next !== 'IN' && next !== 'INTL') return;
    setRegionState(next);
    setChosen(true);
    try {
      window.localStorage.setItem(REGION_STORAGE_KEY, next);
    } catch {
      // storage blocked
    }
  }, []);

  const value = {
    region,
    regionInfo: REGIONS[region],
    isInternational: region === 'INTL',
    hasChosenRegion: chosen,
    setRegion,
  };

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
}

export function useRegion() {
  const ctx = useContext(RegionContext);
  if (!ctx) throw new Error('useRegion must be used within a RegionProvider');
  return ctx;
}

// ── Price helpers ─────────────────────────────────────────────────────────
// USD uses the same fixed rate as the PayPal order (catalog.inrToUsd), so the
// price a shopper sees is exactly what PayPal charges.

export function formatInr(amount) {
  return `₹${Number(amount || 0).toLocaleString('en-IN')}`;
}

// Whole-dollar catalog prices show without cents; bulk-discounted amounts keep them.
export function formatUsd(amount) {
  const cents = Math.round(Number(amount || 0) * 100);
  const digits = cents % 100 === 0 ? 0 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(cents / 100);
}

/** Format an INR catalog amount in the shopper's region currency. */
export function formatRegionPrice(inrAmount, region) {
  if (region === 'INTL') return formatUsd(catalog.inrToUsd(inrAmount));
  return formatInr(inrAmount);
}